<script>
import {
    fade
} from 'svelte/transition';
import {
    modalState, hideModal
} from '@app/store/modalStore.svelte.js';
import {
    insertHistoryAction
} from '@app/store/historyStore.svelte.js';
import {
    eventGroupState
} from '@app/store/eventGroupStore.svelte.js';
import {
    authState
} from '@app/store/authStore.svelte.js';
import {
    environment
} from '@app/store/envStore.svelte.js';
import {
    styleState
} from '@app/store/styleStore.svelte.js';
import {
    projectState
} from '@app/store/projectStore.svelte.js';
import EventGroup from '@cclib/classes/eventGroup.js';
import LanguageSelect from '@app/components/inputs/LanguageSelect.svelte';
import insertEvent from '@cclib/functions/eventGroups/insertEvent.js';
import * as m from '@app/paraglide/messages.js';
let eventGroupDefaults = JSON.parse(localStorage.getItem("cc-event-group-defaults")) || {};
let eventGroupType = $state(eventGroupDefaults.eventGroupType || "subtitle"),
    displayName = $state(""),
    language = $state(eventGroupDefaults.language || "en-US"),
    rtl = $state(eventGroupDefaults.rtl),
    fontFamily = $state(eventGroupDefaults.fontFamily || "plexMono"),
    fontSize = $state(eventGroupDefaults.fontSize || 16),
    overlap = $state(eventGroupDefaults.overlap),
    maxCps = $state(eventGroupDefaults.maxCps || 25),
    maxWpm = $state(eventGroupDefaults.maxWpm || 250),
    maxChars = $state(eventGroupDefaults.maxChars || 32),
    maxLines = $state(eventGroupDefaults.maxLines || 2),
    minDuration = $state(eventGroupDefaults.minDuration || 1),
    maxDuration = $state(eventGroupDefaults.maxDuration || 12),
    illegalChars = $state(eventGroupDefaults.illegalChars),
    linkedGroupId = $state(), //Translation Types only
    canCreateAudioDescription = $derived(authState.ad || !environment.online);

function createEventGroup() {
    let eventGroupOptions = {
        type: eventGroupType,
        originalFontFamily: styleState.fontFamily,
        originalRtl : rtl,
        name: displayName,
        language: language,
        linkedGroup: linkedGroupId,
        rtl: rtl,
        fontFamily: fontFamily,
        fontSize: fontSize || 16,
        illegalChars : illegalChars,
        overlap: overlap,
        maxCps: maxCps || 25,
        maxWpm: maxWpm || 250,
        maxChars: maxChars || 32,
        maxLines: maxLines || 2,
        minDuration : minDuration || 1,
        maxDuration : maxDuration || 12
    }

    // Create a new event group and push it to the state
    const newEventGroup = {...(new EventGroup(eventGroupOptions))};
    eventGroupState.push(newEventGroup);
    
    projectState.selected = eventGroupState.length - 1;
    
    if (eventGroupType === 'translation') {
        let linkedGroupIndex = eventGroupState.findIndex(evg =>{
            return evg.id === linkedGroupId;
        });

        if (linkedGroupIndex > -1){
            eventGroupState[linkedGroupIndex].events.forEach(event => {
                // Push each event to the events array instead of reassigning
                eventGroupState[projectState.selected].events.push({
                    ...event,
                    original : event.text,
                    text : ""
                });
            });

            eventGroupState[projectState.selected].originalRtl = eventGroupState[projectState.selected].rtl;
        }        
    } else if (eventGroupType !== 'subtitle' && eventGroupType !== 'translation') {
        // Instead of direct assignment, store the result and update specific properties
        const result = insertEvent(eventGroupState[projectState.selected], {
            alignment: "left"
        });
        
        // Copy the events array from the result to the existing event group
        eventGroupState[projectState.selected].events = [...result.events];
        
        // Copy any other properties that might have changed
        if (result.selected) {
            eventGroupState[projectState.selected].selected = [...result.selected];
        }
    } else {
        // Instead of direct assignment, store the result and update specific properties
        const result = insertEvent(eventGroupState[projectState.selected], {
            alignment: "center"
        });
        
        // Copy the events array from the result to the existing event group
        eventGroupState[projectState.selected].events = [...result.events];
        
        // Copy any other properties that might have changed
        if (result.selected) {
            eventGroupState[projectState.selected].selected = [...result.selected];
        }
    }

    localStorage.setItem("cc-event-group-defaults", JSON.stringify({
        eventGroupType: eventGroupType,
        name: displayName,
        language: language,
        fontFamily: fontFamily,
        fontSize: fontSize || 16,
        overlap : overlap,
        illegalChars : illegalChars,
        maxCps : maxCps || 25,
        maxWpm : maxWpm || 250,
        maxChars : maxChars || 32,
        maxLines : maxLines || 2,  
        minDuration : minDuration >= 0 ? minDuration : 0.5,
        maxDuration : maxDuration || 12,
        rtl: rtl
    }));

    insertHistoryAction({
        name: "insert event group", //action name
        eventGroup: projectState.selected,
        snapshots: [{
            store: "eventGroupState",
            value: JSON.stringify(eventGroupState)
        }]
    });
    hideModal();
}
</script>

<div transition:fade={{duration: 100}} 
    class="modal {modalState.selected === 'newEventGroup' ? 'show d-block' : ''}" 
    role="dialog"
    aria-labelledby="newEventGroupTitle"
    aria-describedby="newEventGroupDescription" 
    tabindex="-1" 
    id="NewEventGroupModal">
    <div class="modal-dialog modal-lg modal-dialog-centered" role="document">
        <div class="modal-content">
            <div class="modal-header">
                <h4 class="modal-title" id="newEventGroupTitle">{m.new_event_group()}</h4>
                <button type="button" class="btn-close" id="newEventGroupCloseBtn" aria-label={m.close_new_event_group_modal()} onclick={hideModal}></button>
            </div>
            <div class="modal-body">
                <form onsubmit={() => createEventGroup()} id="NewEventGroupForm">
                    <!-- Group Type -->
                    <div class="mb-3">
                        <label class="form-label" for="groupTypeSelect">{m.group_type()}</label>
                        <select class="form-select" id="groupTypeSelect" bind:value={eventGroupType} aria-describedby="groupTypeDescription">
                            <option value="subtitle">{m.subtitle()}</option>
                            {#if canCreateAudioDescription}
                            <option value="audio description">{m.audio_description()}</option>
                            {/if}
                            <option value="transcription">{m.transcription()}</option>
                            <option value="translation">{m.translation()}</option>
                        </select>
                    </div>
                    <!-- Name -->
                    <div class="mb-3"><label class="form-label" for="displayNameInput">{m.display_name()}</label>
                        <input class="form-control" 
                               id="displayNameInput"
                               required 
                               placeholder={m.display_name()} 
                               type="text" 
                               bind:value={displayName}
                               aria-required="true"
                               aria-invalid={!displayName}>
                    </div>
                    <!-- Language -->
                    <div class="mb-3">
                        <label class="form-label" for="Language">{m.language()}</label>
                        <LanguageSelect id="Language" bind:value={language} />
                    </div>
                    <div class="form-check form-switch mb-2">
                        <input type="checkbox" class="form-check-input" id="rtlSwitch" bind:checked={rtl}>
                        <label class="form-check-label" for="rtlSwitch">{m.right_to_left()}</label>
                    </div>
                    <div>
                        <hr/>
                        <p class="lead text-warning m-0">{m.error_detection()}</p>
                        <div class="row">
                            <div class="col-6 mb-3">
                                <label class="form-label" for="Maximum characters per second">{m.max_characters_per_second()}</label>
                                <input type="number" step="1" min="0" max="9999" class="form-control" bind:value={maxCps}>
                            </div>
                            <div class="col-6 mb-3">
                                <label class="form-label" for="Maximum words per minute">{m.max_words_per_minute()}</label>
                                <input type="number" step="1" min="0" max="9999" class="form-control" bind:value={maxWpm}>
                            </div>
                            <div class="col-6 mb-3">
                                <label class="form-label" for="Maximum characters per line">{m.max_characters_per_line()}</label>
                                <input type="number" step="1" min="0" max="9999" class="form-control" bind:value={maxChars}>
                            </div>
                            <div class="col-6 mb-3">
                                <label class="form-label" for="Maximum lines per event">{m.max_lines_per_event()}</label>
                                <input type="number" step="1" min="0" max="9999" class="form-control" bind:value={maxLines}>
                            </div>
                            <div class="col-6 mb-3">
                                <label class="form-label" for="Minimum Event Duration">{m.min_event_duration()}</label>
                                <input type="number" step="0.01" min="0" max="9999" class="form-control" bind:value={minDuration}>
                            </div>
                            <div class="col-6 mb-3">
                                <label class="form-label" for="Maximum Event Duration">{m.max_event_duration()}</label>
                                <input type="number" step="0.01" min="0" max="9999" class="form-control" bind:value={maxDuration}>
                            </div>
                            <div class="col-6 mb-3">
                                <div class="form-check form-switch">
                                    <input type="checkbox" class="form-check-input" id="overlapCheck" bind:checked={overlap}>
                                    <label class="form-check-label" for="overlapCheck">{m.overlap_detection()}</label>
                                </div>
                            </div>
                            <div class="col-6 mb-3">
                                <div class="form-check form-switch">
                                    <input type="checkbox" class="form-check-input" id="illegalCharsCheck" bind:checked={illegalChars}>
                                    <label class="form-check-label" for="illegalCharsCheck">{m.detect_illegal_characters_608()}</label>
                                </div>
                            </div>
                        </div>
                    </div>
                    <!-- Linked Group -->
                    {#if eventGroupType === 'translation'}
                    <div transition:fade={{duration: 100 }} role="region" aria-labelledby="translationOptionsTitle">
                        <hr/>
                        <p class="lead text-warning m-0" id="translationOptionsTitle">{m.translation_options()}</p>
                        <div class="mb-3">
                            <label class="form-label" for="linkedGroupSelect">
                                <span class="badge bg-danger">{m.required()}</span> {m.linked_group()} 
                                <i class="bi bi-info-circle" aria-label={m.linked_group_help()}></i>
                            </label>
                            <select class="form-select" 
                                    id="linkedGroupSelect"  
                                    bind:value={linkedGroupId}
                                    aria-required="true"
                                    aria-invalid={eventGroupType === 'translation' && !linkedGroupId}>
                                {#each eventGroupState as eventGroup (eventGroup.id)}
                                <option value={eventGroup.id}>{eventGroup.name}</option>
                                {/each}
                            </select>
                        </div>
                    </div>
                    {/if}
                </form>
            </div>
            <div class="modal-footer">
                <button class="btn btn-primary" 
                        id="createGroupBtn"
                        type="button" 
                        disabled={eventGroupType === 'translation' && linkedGroupId === undefined} 
                        onclick={() => createEventGroup()}
                        aria-label={m.create_new_event_group()}>
                    {m.create_group()}
                </button>
            </div>
        </div>
    </div>
</div>
